import { childCount, fmtDate, projectCode, type Board, type BoardItem, type PlatformBoards } from "@boem/core/shared";
import { useCallback, useEffect, useState } from "react";
import { ApiError, call } from "../api.ts";
import { boardHref } from "../boards.ts";
import { apiBase } from "../org.ts";
import { ErrorLine, Modal } from "./Modal.tsx";

// A board's life on screen (docs/design/orgs.md 1.5): the frozen banner, where an item came from
// (moved here by a platform admin, or copied), Copy to my board, and the platform admin's list of
// boards that need an owner or were closed. A platform admin sees names, owners, dates, counts and
// people, never a board's items.

export const lifecycleApi = {
  copyToMine: (num: number, opts: { notes: boolean; shots: boolean; subItems: boolean; archiveSource: boolean; parent: number | null }) =>
    call<{ num: number; slug: string }>("POST", `${apiBase()}/items/${num}/copy-to-mine`, opts),
  closeBoard: () => call("POST", `${apiBase()}/close`, {}),
  restoreMine: () => call<{ slug: string }>("POST", "/api/orgs/me/restore", {}),
  platform: () => call<PlatformBoards>("GET", "/api/platform/boards"),
  handOver: (id: string, memberId: string) => call("POST", `/api/platform/boards/${encodeURIComponent(id)}/owner`, { memberId }),
  move: (id: string, to: string) => call<{ items: number }>("POST", `/api/platform/boards/${encodeURIComponent(id)}/move`, { to }),
  close: (id: string) => call("POST", `/api/platform/boards/${encodeURIComponent(id)}/close`, {}),
  restore: (id: string) => call("POST", `/api/platform/boards/${encodeURIComponent(id)}/restore`, {}),
};

/** "Gautam left the company. This board is read-only until someone is made its owner." */
export function FrozenBanner({ frozen }: { frozen: NonNullable<Board["org"]["frozen"]> }) {
  const who = frozen.formerOwners.length ? frozen.formerOwners.join(" and ") : "Its owner";
  return (
    <div className="frozen-banner" role="status">
      <b>{who} left the company.</b>{" "}
      This board is read-only until someone is made its owner (since {fmtDate(frozen.since)}). Your pins still work.
    </div>
  );
}

/** "was Gautam's #5 · 01-03": an item a platform admin moved here, by its numbers there (text from then). */
export const movedLabel = (m: NonNullable<BoardItem["movedFrom"]>[number]) => `was ${m.board.replace(/ board$/, "")} #${m.num}${m.key ? ` · ${m.key}` : ""}`;

/** "copied from Gautam's board #5 · 01-03", with a link back while the reader can open that board. */
export function CopiedFrom({ c }: { c: NonNullable<BoardItem["copiedFrom"]> }) {
  const text = `copied from ${c.board} #${c.num}${c.key ? ` · ${c.key}` : ""}`;
  return (
    <div className="copied-from">
      {c.href ? (
        <a href={c.href} target="_blank" rel="noopener" title="Open the original on its board, in a new tab">
          {text}
        </a>
      ) : (
        <span>{text}</span>
      )}
      {c.closed ? <span className="cf-note"> · source board closed</span> : !c.href ? <span className="cf-note"> · you can&rsquo;t open that board</span> : null}
    </div>
  );
}

/** Copy to my board: options, then the copy on my board (its own next number). */
export function CopyToMineModal({ item, mySlug, canArchive, onClose, onDone }: { item: BoardItem; mySlug: string; canArchive: boolean; onClose: () => void; onDone: (r: { num: number; slug: string }) => void }) {
  const [notes, setNotes] = useState(item.notes.length > 0);
  const [shots, setShots] = useState(item.shots.some((s) => s.scan === "clean" || s.scan === "unscanned"));
  const [subItems, setSubItems] = useState(childCount(item) > 0);
  const [archive, setArchive] = useState(canArchive);
  const [projects, setProjects] = useState<BoardItem[] | null>(null);
  const [parent, setParent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    call<Board>("GET", `/api/orgs/${encodeURIComponent(mySlug)}/board`).then(
      (b) => setProjects(b.items.filter((i) => i.kind === "project" && i.status === "open")),
      () => setProjects([]),
    );
  }, [mySlug]);
  const cleared = item.shots.filter((s) => s.scan === "clean" || s.scan === "unscanned").length;
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await lifecycleApi.copyToMine(item.num, { notes, shots, subItems, archiveSource: archive, parent });
      onDone(r);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const ref = item.key?.label ?? `#${item.num}`;
  return (
    <Modal onClose={onClose}>
      <div className="org-dlg">
        <h3>
          Copy {ref} to my board
        </h3>
        <p className="ppl-lead">
          A new item on your board, with its own next number. It says where it came from (&ldquo;copied from this board {ref}&rdquo;); later changes here don&rsquo;t follow it.
        </p>
        <div className="fl">Put it in</div>
        <select className="search" aria-label="Put it in" value={parent ?? ""} onChange={(e) => setParent(e.target.value ? Number(e.target.value) : null)}>
          <option value="">No project</option>
          {(projects ?? []).map((p) => (
            <option key={p.num} value={p.num}>
              {p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} {p.title}
            </option>
          ))}
        </select>
        <label className="tm-check">
          <input type="checkbox" checked={notes} disabled={!item.notes.length} onChange={(e) => setNotes(e.target.checked)} /> Copy its notes as one note ({item.notes.length})
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={shots} disabled={!cleared} onChange={(e) => setShots(e.target.checked)} /> Copy screenshots ({cleared} cleared by the scan)
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={subItems} disabled={!childCount(item)} onChange={(e) => setSubItems(e.target.checked)} /> Copy its sub-items ({childCount(item)})
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={archive} disabled={!canArchive} onChange={(e) => setArchive(e.target.checked)} /> Archive it here afterwards
          {!canArchive && <span className="h"> (your role here can&rsquo;t archive)</span>}
        </label>
        <ErrorLine error={error} />
        <div className="row">
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={go}>
            Copy to my board
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** The platform admin's screen: boards that need an owner (hand over, move items), closed boards (restore). */
export function PlatformModal({ onClose, toast }: { onClose: () => void; toast: (m: string) => void }) {
  const [data, setData] = useState<PlatformBoards | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState<Record<string, string>>({});
  const [target, setTarget] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => lifecycleApi.platform().then(setData, (e) => setError(e instanceof ApiError ? e.message : "Couldn't reach the server.")), []);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (fn: () => Promise<unknown>, said: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast(said);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg platform">
        <div className="dlg-eyebrow">Platform admin</div>
        <h3>Boards that need an owner</h3>
        <p className="ppl-lead">
          A board freezes when its last owner leaves the company. Make someone its owner (the board, its numbers and links stay as they are), or move its items into another board (they get its next numbers and keep their old ones as &ldquo;was …&rdquo;). You see who&rsquo;s on a board and how many items it has, never the items.
        </p>
        {!data ? (
          <div className="empty">Loading&hellip;</div>
        ) : (
          <>
            {data.frozen.length === 0 && <p className="ppl-note">None right now.</p>}
            <ul className="ppl pf-list">
              {data.frozen.map((b) => (
                <li key={b.id}>
                  <span className="nm">{b.name}</span>
                  <span className="role-fixed">{b.items} items</span>
                  <span className="sub">
                    Frozen since {fmtDate(b.frozenAt)} · former owners: {b.formerOwners.join(", ") || "none"}
                    {b.people.length ? ` · on it: ${b.people.map((p) => `${p.name} (${p.role})`).join(", ")}` : " · nobody else on it"}
                  </span>
                  <span className="pf-acts">
                    <select className="search" aria-label={`New owner of ${b.name}`} value={pick[b.id] ?? ""} onChange={(e) => setPick({ ...pick, [b.id]: e.target.value })}>
                      <option value="">Make someone its owner…</option>
                      {data.people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn" disabled={busy || !pick[b.id]} onClick={() => act(() => lifecycleApi.handOver(b.id, pick[b.id]!), `${b.name} has an owner again.`)}>
                      Hand over
                    </button>
                    <select className="search" aria-label={`Move ${b.name}'s items to`} value={target[b.id] ?? ""} onChange={(e) => setTarget({ ...target, [b.id]: e.target.value })}>
                      <option value="">Move its items to…</option>
                      {data.targets.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy || !target[b.id]}
                      onClick={() =>
                        window.confirm(`Move every item of ${b.name} to the board you picked, and close ${b.name}? Each item keeps its old number there as "was …".`) &&
                        act(() => lifecycleApi.move(b.id, target[b.id]!), `${b.name}'s items moved; the board is closed.`)
                      }
                    >
                      Move items
                    </button>
                    <button type="button" className="chip sm" disabled={busy} onClick={() => window.confirm(`Close ${b.name}? It's kept, and can be restored.`) && act(() => lifecycleApi.close(b.id), `${b.name} is closed.`)}>
                      Close
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <div className="fl">Closed boards</div>
            {data.closed.length === 0 && <p className="ppl-note">None.</p>}
            <ul className="ppl pf-list">
              {data.closed.map((b) => (
                <li key={b.id}>
                  <span className="nm">{b.name}</span>
                  <span className="role-fixed">{b.items} items</span>
                  <span className="sub">
                    Closed {fmtDate(b.closedAt)}
                    {b.mergedInto ? ` · its items moved to ${b.mergedInto}` : ""} · owners: {[...b.owners, ...b.formerOwners].join(", ") || "none"}
                  </span>
                  {!b.mergedInto && (
                    <span className="pf-acts">
                      <button type="button" className="btn" disabled={busy} onClick={() => act(() => lifecycleApi.restore(b.id), `${b.name} is back.`)}>
                        Restore
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        <ErrorLine error={error} />
        <div className="row">
          <span className="sp" />
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** A link to a board (the switcher's): for the toasts that say where a copy went. */
export const boardLink = (slug: string, num: number) => `${boardHref(slug)}${slug === "oemuap" ? "?" : "&"}panels=item:${num}`;
